import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStores } from "../../use-stores";
import { useNotification } from "../../utils/NotificationsContext";
import CustomTypography from "../components/common/form/CustomTypography";

// Prevent duplicate callback execution for the same one-time Google auth code.
const processedGoogleCodes = new Set<string>();

const GoogleSuccessPopup = () => {
  const { userStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusMessage, setStatusMessage] = useState("Verifying your Google account...");

  useEffect(() => {
    const executeGoogleCallback = async () => {
      const code = searchParams.get("code");

      if (!code) {
        showError("Error", "Google authorization code is missing.");
        navigate("/login", { replace: true });
        return;
      }

      if (processedGoogleCodes.has(code)) {
        return;
      }
      processedGoogleCodes.add(code);

      try {
        const result = await userStore.userAPI.googleAuthCallback(code);

        const token = result?.token;
        if (token && typeof token === "string") {
          const tokenKey =
            process.env.REACT_APP_TOKEN_KEY || "Bearer_token#localhost";
          localStorage.setItem(tokenKey, token);
          await userStore.getUserDetails();
          navigate("/", { replace: true });
          return;
        }

        const flowStatus =
          result?.status || result?.state || result?.next_step || "pending";
        setStatusMessage(`Google auth completed with status: ${flowStatus}.`);
      } catch (_error) {
        processedGoogleCodes.delete(code);
        const error = _error as any;
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.result?.message ||
          error?.message ||
          "Google authentication callback failed.";
        showError("Error", backendMessage);
        navigate("/login", { replace: true });
      }
    };

    executeGoogleCallback();
  }, [navigate, searchParams, showError, userStore]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: "#b9c0cb",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 360,
          minHeight: 240,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          py: 2.2,
        }}
      >
        <Box sx={{ position: "relative", mb: 2.2 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background:
                "linear-gradient(135deg, rgba(91,119,236,0.95) 15%, rgba(36,151,88,0.95) 85%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 18px rgba(72, 99, 204, 0.28)",
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#5b77ec",
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1,
              }}
            >
              G
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              width: 24,
              height: 6,
              borderRadius: "999px",
              backgroundColor: "#5b77ec",
              left: "50%",
              bottom: -5,
              transform: "translateX(-50%) rotate(-8deg)",
              opacity: 0.9,
            }}
          />
        </Box>

        <CustomTypography variant="heading4" style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.8rem" }}>
          Authenticating with Google
        </CustomTypography>
        <CustomTypography variant="body2" style={{ fontSize: "0.9rem", color: "#64748b", marginBottom: "2.5rem" }}>
          {statusMessage}
        </CustomTypography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {[0, 1, 2].map((dot) => (
            <Box
              key={dot}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#6680e9",
                animation: "pulseDot 1.2s ease-in-out infinite",
                animationDelay: `${dot * 0.2}s`,
                "@keyframes pulseDot": {
                  "0%, 80%, 100%": { transform: "scale(0.8)", opacity: 0.6 },
                  "40%": { transform: "scale(1.12)", opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default GoogleSuccessPopup;
